"use client";

import { useCallback, useMemo, useState } from "react";

import type { SongControler } from "@/lib/track/controler/song-controler";

/**
 * Ce que le morceau fait sonner.
 *
 * Même objet que le `setSongVoice` du parcours guidé (`use-rhythm-run`), avec
 * la main nommée plutôt que déduite d'un `accompanyHand` posé ailleurs : ici
 * c'est l'utilisateur qui la choisit, il n'y a rien à déduire.
 *
 * Les membres sont ceux de `HandMode` (`hand-selector`), pour que l'état se
 * branche directement sur le sélecteur.
 */
export type SongListeningMode = "none" | "left" | "both" | "right";

export type SongListening = {
  /**
   * Vrai quand les voies du morceau portent les mains. Sans elles, `left` et
   * `right` ne désignent rien et le contrôle n'a pas lieu d'être affiché.
   */
  available: boolean;
  mode: SongListeningMode;
  setMode: (mode: SongListeningMode) => void;
};

/**
 * Pilote l'audio du morceau, et uniquement l'audio : `soloHand` / `setMuted`
 * ne touchent qu'au `mute` des voies, jamais à leur visibilité.
 */
export function useSongListening(
  songControler: SongControler | null,
): SongListening {
  // `load()` remplit les voies avant que le contrôleur n'atteigne le state de
  // `StudioPage`, la réponse est donc stable pour un contrôleur donné.
  const available = useMemo(
    () => songControler?.hasHandLanes() ?? false,
    [songControler],
  );

  const [mode, setModeState] = useState<SongListeningMode>("both");

  // Retour à « Deux » au chargement d'un morceau. Un `SongControler` neuf a
  // toutes ses voies audibles : seul l'état local a besoin de rattraper, rien
  // à repousser vers l'audio. Ajusté pendant le rendu plutôt que dans un
  // effet, pour ne pas peindre une frame avec l'état du morceau précédent.
  const [lastControler, setLastControler] = useState(songControler);

  if (lastControler !== songControler) {
    setLastControler(songControler);
    setModeState("both");
  }

  const setMode = useCallback(
    (next: SongListeningMode) => {
      setModeState(next);

      if (songControler === null) {
        return;
      }

      if (next === "left" || next === "right") {
        songControler.soloHand(next);

        return;
      }

      songControler.setMuted(next === "none");
    },
    [songControler],
  );

  return { available, mode, setMode };
}
